import { FAMILY_LABEL, type SignalDefinition } from "@ti/core";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, Lightbulb, ListChecks, MinusCircle, PlusCircle, Sparkles } from "lucide-react";
import { useMemo } from "react";
import { PrecisionBadge, SignalDefinitionStatusBadge, SignalTierBadge } from "@/components/domain/analytics";
import { SERIES, Sparkline } from "@/components/domain/charts";
import { KeyValue, PageHeader, TimeAgo } from "@/components/domain/misc";
import { Delta } from "@/components/domain/score";
import { SeverityPill } from "@/components/domain/status";
import { ErrorPanel, Page, PageSkeleton } from "@/components/layout/page";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/misc";
import { SessionLink, SessionsLink } from "@/features/analytics-shared/links";
import { BacktestPanel } from "@/features/signals/backtest-panel";
import { DetectionTierBadge } from "@/features/signals/detection-tier";
import { CertificationCard, LifecycleActions, LifecycleCard, VersionHistory } from "@/features/signals/lifecycle";
import { METHOD_META, MethodBadge, OriginLabel, scopeLabel, trendDelta } from "@/features/signals/meta";
import { q } from "@/lib/api";
import { fmtCost, fmtInt, fmtPct } from "@/lib/format";
import { usePackName } from "@/lib/packs";

export const Route = createFileRoute("/_app/signals/studio/$definitionId")({
  component: DefinitionPage,
});

function DefinitionPage() {
  const { definitionId } = Route.useParams();
  const def = useQuery(q.signalDefinition(definitionId));
  const agents = useQuery(q.agents());
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);

  if (def.error)
    return (
      <Page>
        <Breadcrumb name={definitionId} />
        <ErrorPanel error={def.error} reset={() => void def.refetch()} />
      </Page>
    );
  if (!def.data) return <PageSkeleton />;
  const d = def.data;

  return (
    <Page>
      <PageHeader
        breadcrumb={<Breadcrumb name={d.name} />}
        title={d.name}
        description={
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <span className="font-mono text-[12px] text-fg-2">{d.type}</span>
            <span className="font-mono text-[11.5px] text-fg-3">v{d.version}</span>
            <SignalDefinitionStatusBadge status={d.status} />
            <SignalTierBadge tier={d.tierClass} certification={d.certification} />
            <SeverityPill severity={d.severity} />
            <MethodBadge method={d.method} />
            <OriginLabel origin={d.origin} />
          </span>
        }
        actions={<LifecycleActions def={d} />}
      />

      {d.proposal ? <ProposalEvidence def={d} /> : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          <DefinitionCard def={d} names={names} />
          <ExamplesCard def={d} />
          <BacktestPanel def={d} />
        </div>
        <div className="flex flex-col gap-4">
          <LifecycleCard def={d} />
          <PerformanceCard def={d} />
          <CertificationCard def={d} />
          <VersionHistory def={d} />
        </div>
      </div>
    </Page>
  );
}

function Breadcrumb({ name }: { name: string }) {
  return (
    <nav aria-label="Breadcrumb">
      <Link to="/signals" className="hover:text-fg">
        Signals
      </Link>
      <span className="px-1.5">/</span>
      <Link to="/signals/studio" className="hover:text-fg">
        Studio
      </Link>
      <span className="px-1.5">/</span>
      <span className="text-fg-2">{name}</span>
    </nav>
  );
}

function ProposalEvidence({ def }: { def: SignalDefinition }) {
  const p = def.proposal!;
  return (
    <Card className="mb-4 border-accent/30">
      <CardHeader
        title={
          <span className="inline-flex items-center gap-1.5">
            <Sparkles className="size-3.5 text-accent" aria-hidden /> Discovered in your sessions
          </span>
        }
        description={
          <>
            Found <TimeAgo iso={p.discoveredAt} className="text-fg-3" /> · {fmtInt(p.clusterSessions)} sessions in the cluster · {fmtPct(p.unexplainedShare)} not explained by any signal or issue
          </>
        }
      />
      <CardBody className="grid gap-4 md:grid-cols-3">
        <div>
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Outcome lift</h4>
          <p className="mt-0.5 text-[13px] font-medium text-fg">{p.outcomeLift}</p>
        </div>
        <div>
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-fg-3">Reasoning</h4>
          <p className="mt-0.5 text-xs text-fg-2">{p.reasoning}</p>
        </div>
        <div>
          <h4 className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-fg-3">
            <Lightbulb className="size-3" aria-hidden /> Suggested fix
          </h4>
          <p className="mt-0.5 text-xs text-fg-2">{p.suggestedFix}</p>
          <div className="mt-2 flex flex-wrap gap-x-2.5 gap-y-1">
            {p.sampleSessionIds.map((id) => (
              <SessionLink key={id} id={id} />
            ))}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

const THRESHOLD_SOURCE: Record<SignalDefinition["thresholdSource"], string> = {
  default: "default until 20 reviews",
  calibrated: "tuned from reviews to 80% precision",
  manual: "set by hand",
};

function DefinitionCard({ def, names }: { def: SignalDefinition; names: Map<string, string> }) {
  const packName = usePackName();
  const m = METHOD_META[def.method];
  return (
    <Card>
      <CardHeader title="Rubric" description={def.method === "decision" ? "Asked as a yes/no question in every session's decision pass" : def.method === "llm_rubric" ? "Applied by the generative pass on sampled and flagged sessions" : "What the detector matches"} />
      <CardBody className="flex flex-col gap-4">
        <blockquote className="whitespace-pre-line rounded-md border-l-2 border-accent bg-surface-2 px-3 py-2.5 text-[13.5px] text-fg">{def.description}</blockquote>
        <KeyValue
          items={[
            ["Family", FAMILY_LABEL[def.family]],
            ["Method", <span key="m" className="text-fg-2">{m.label} · <span className="text-fg-3">{m.how}</span></span>],
            [
              "Threshold",
              def.method === "rules" ? (
                <span key="th" className="text-fg-3">Fires on a match</span>
              ) : (
                <span key="th" className="tabular">
                  p ≥ {def.threshold.toFixed(2)} <span className="text-fg-3">· {THRESHOLD_SOURCE[def.thresholdSource]}</span>
                </span>
              ),
            ],
            ["Detection", <DetectionTierBadge key="t" tier={def.detectionTier} withName />],
            ["Scope", <span key="s" className={def.agentIds ? "font-mono text-[12px]" : undefined}>{scopeLabel(def.agentIds, names)}</span>],
            ["Pack", packName(def.pack)],
            ["Creates issues", def.createsIssues ? "Yes, when active" : "No"],
            ["Created", <span key="c">{def.createdBy} · <TimeAgo iso={def.createdAt} /></span>],
          ]}
        />
      </CardBody>
    </Card>
  );
}

function ExamplesCard({ def }: { def: SignalDefinition }) {
  const pos = def.examples.filter((e) => e.positive);
  const neg = def.examples.filter((e) => !e.positive);
  const list = (items: typeof def.examples, positive: boolean) =>
    items.length ? (
      <ul className="flex flex-col gap-1.5">
        {items.map((e, i) => (
          <li key={i} className="rounded-md border bg-surface-2 px-2.5 py-1.5">
            <p className="text-[13px] text-fg-2">“{e.text}”</p>
            {e.sessionId ? <SessionLink id={e.sessionId} className="mt-0.5" /> : null}
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-xs text-fg-3">{positive ? "No positive examples yet." : "No negative examples yet. Add near-misses so the threshold can be tuned."}</p>
    );
  if (!def.examples.length)
    return (
      <Card>
        <CardHeader title="Examples" />
        <EmptyState title="No examples" description="Examples help calibrate the threshold. Labels from the review queue become examples too." className="py-8" />
      </Card>
    );
  return (
    <Card>
      <CardHeader title="Examples" description="Should fire vs shouldn't fire. Reviewed hits are added as they're labeled." />
      <CardBody className="grid gap-4 md:grid-cols-2">
        <div>
          <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-fg-2">
            <PlusCircle className="size-3.5 text-good" aria-hidden /> Should fire <span className="tabular text-fg-3">{pos.length}</span>
          </h4>
          {list(pos, true)}
        </div>
        <div>
          <h4 className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-fg-2">
            <MinusCircle className="size-3.5 text-critical" aria-hidden /> Shouldn't fire <span className="tabular text-fg-3">{neg.length}</span>
          </h4>
          {list(neg, false)}
        </div>
      </CardBody>
    </Card>
  );
}

function PerformanceCard({ def }: { def: SignalDefinition }) {
  const t = def.hitTrend;
  const reviewable = def.status === "shadow" || def.status === "active" || def.status === "paused";
  return (
    <Card>
      <CardHeader title="Live performance" description="Last 7 days of new sessions" />
      <CardBody className="flex flex-col gap-3">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="text-2xl font-semibold leading-none tabular">{fmtInt(def.hits7d)}</div>
            <div className="mt-1 text-xs text-fg-3">
              hits · <Delta value={trendDelta(t)} suffix="% vs prior 7d" invert />
            </div>
          </div>
          {t.some((x) => x > 0) ? <Sparkline data={t} width={120} height={32} color={SERIES[0]} label={`Daily hits over 14 days, from ${t[0]} to ${t[t.length - 1]}`} /> : null}
        </div>
        <KeyValue
          items={[
            ["Precision", <PrecisionBadge key="p" value={def.precision} labeled={def.labeled} />],
            ["Cost", <span key="c" className="tabular">{fmtCost(def.costPer1k)} per 1,000 sessions</span>],
            ["Updated", <TimeAgo key="u" iso={def.updatedAt} />],
          ]}
        />
        <div className="flex flex-col gap-1.5 border-t pt-3">
          {reviewable && def.tierClass === "analytics" ? (
            <Link to="/signals/review" search={{ definitionId: def.id }} className="inline-flex items-center gap-1 text-xs text-fg-2 hover:text-accent hover:underline">
              <ListChecks className="size-3.5 text-fg-3" aria-hidden /> Review hits in the queue <ArrowUpRight className="size-3.5 text-fg-3" aria-hidden />
            </Link>
          ) : null}
          <SessionsLink filter={{ signal: def.type }}>Sessions with this signal</SessionsLink>
          {def.tierClass === "risk" ? (
            <Link to="/traces" search={{ q: `signal:${def.type}` }} className="inline-flex items-center gap-1 text-xs text-fg-2 hover:text-accent hover:underline">
              Matching traces <ArrowUpRight className="size-3.5 text-fg-3" aria-hidden />
            </Link>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
